// One normalised slab shape for the viewer, whatever endpoint the rows came from.
// Accepts the business inventory DTO (camelCase), personal vault rows (snake_case)
// and richer browse rows (population / last-sold fields) without extra wiring.

export const ACE_IMAGE_CDN = 'https://collectible-images.ams3.cdn.digitaloceanspaces.com/public/collectible';
const GRADER_PATTERN = /^(ACE|PSA|CGC|BGS|BECKETT|SGC|TAG)\b[\s:-]*/i;

function get(source, path) {
  let current = source;
  for (const key of path.split('.')) {
    if (current == null || typeof current !== 'object') return undefined;
    current = current[key];
  }
  return current;
}

function first(source, paths) {
  for (const path of paths) {
    const value = get(source, path);
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return null;
}

export function toNumber(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string' || !value.trim()) return null;
  const parsed = Number(value.replace(/[£$€,\s]/g, ''));
  return Number.isFinite(parsed) ? parsed : null;
}

function text(value) {
  return value == null ? '' : String(value).trim();
}

function safeImageUrl(value) {
  if (typeof value !== 'string' || !value.trim()) return null;
  if (value.startsWith('/') && !value.startsWith('//')) return value;
  if (/^data:image\/(png|jpe?g|webp|gif|svg\+xml)[;,]/i.test(value)) return value;
  try {
    const url = new URL(value);
    return ['https:', 'http:'].includes(url.protocol) && !url.username && !url.password ? url.href : null;
  } catch {
    return null;
  }
}

function isoDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function savedStorage(raw) {
  const value=raw.storageLocation;
  if(raw.storageLocationStatus==='unavailable')return {status:'unavailable',label:'Location unavailable',detail:'Refresh to try again',compact:'Location unavailable'};
  if(!value&&raw.storageLocationStatus==='held'&&raw.custody){const holder=typeof raw.custody.holder==='string'&&raw.custody.holder.trim()&&raw.custody.holder.length<=200?raw.custody.holder:null;return {status:'held',label:holder?`With ${holder}`:'Out of its box',detail:holder?'Out of its box · still owned':'Holder not recorded · still owned',compact:holder?`With ${holder}`:'Out of its box'};}
  if(!value)return {status:'unlocated',label:'No saved safe location',detail:'',compact:'No saved location'};
  if(value.kind==='container'){
    if(!/^(?:CASE|BOX|STOR)-(?:0[1-9]|[1-9][0-9])$/.test(value.boxCode||'')||typeof value.name!=='string'||!value.name||value.name.length>60||value.side!=='L'||!Number.isInteger(value.position)||value.position<1||value.status!=='placed')return {status:'unavailable',label:'Location unavailable',detail:'Refresh to try again',compact:'Location unavailable'};
    const custody=value.custodyState==='in_transit'?`In transit${value.custodyLabel?' to '+value.custodyLabel:''}`:value.custodyState==='offsite'?`Offsite${value.custodyLabel?' · '+value.custodyLabel:''}`:value.transferPending?'Prepared for transfer':'';
    return {...value,label:value.name,detail:`${custody?custody+' · ':''}Position ${value.position}`,compact:`${value.name}${custody?' · '+(value.custodyState==='offsite'?'Offsite':value.custodyState==='in_transit'?'In transit':'Prepared'):''} · ${value.position}`};
  }
  const {safeNumber:safe,rowNumber:row,boxNumber:box,side,position,status}=value;
  if(![1,2,3].includes(safe)||!Number.isInteger(row)||row<1||row>(safe===3?7:9)||!Number.isInteger(box)||box<1||box>4||!['L','R'].includes(side)||!Number.isInteger(position)||position<1||!['placed','reserved'].includes(status))return {status:'unavailable',label:'Location unavailable',detail:'Refresh to try again',compact:'Location unavailable'};
  const boxCode=`S${safe}-R${String(row).padStart(2,'0')}-B${String(box).padStart(2,'0')}`;
  if(value.boxCode!==boxCode)return {status:'unavailable',label:'Location unavailable',detail:'Refresh to try again',compact:'Location unavailable'};
  const label=`Safe ${safe} · Row ${row} · Box ${box}`,detail=`${side==='L'?'Left':'Right'} · Position ${position} from the front`;
  return {...value,label:status==='reserved'?`Reserved: ${label}`:label,detail:status==='reserved'?`${detail} · Placement not confirmed`:detail,compact:`${status==='reserved'?'Reserved · ':''}Safe ${safe} · R${row} · B${box} · ${side}${position}`};
}

export function aceImage(cert, side) {
  return /^\d{4,12}$/.test(cert || '') ? `${ACE_IMAGE_CDN}/${cert}/${cert}_${side}.webp` : null;
}

export function parseGrade(rawGrade, rawGrader) {
  const gradeText = text(rawGrade);
  let grader = text(rawGrader).toUpperCase();
  const prefix = gradeText.match(GRADER_PATTERN);
  if (prefix && !grader) grader = prefix[1].toUpperCase();
  const rest = prefix ? gradeText.slice(prefix[0].length) : gradeText;
  const numeric = rest.match(/\d+(?:\.\d+)?/);
  const grade = numeric ? numeric[0] : rest;
  const label = numeric ? rest.replace(numeric[0], '').replace(/^[\s\-·:]+|[\s\-·:]+$/g, '') : '';
  if (grader === 'BECKETT') grader = 'BGS';
  if (!grader && grade) grader = 'ACE';
  return { grader, grade: grade.trim(), gradeNum: numeric ? Number(numeric[0]) : null, gradeLabel: label };
}

const LANGUAGES = {
  en: 'English', eng: 'English', english: 'English',
  ja: 'Japanese', jp: 'Japanese', jpn: 'Japanese', japanese: 'Japanese',
  ko: 'Korean', kr: 'Korean', kor: 'Korean', korean: 'Korean',
  zh: 'Chinese', cn: 'Chinese', chinese: 'Chinese', 's-chinese': 'Chinese', 't-chinese': 'Chinese',
  'simplified chinese': 'Chinese', 'traditional chinese': 'Chinese',
  de: 'German', german: 'German', fr: 'French', french: 'French', it: 'Italian', italian: 'Italian',
  es: 'Spanish', spanish: 'Spanish', pt: 'Portuguese', portuguese: 'Portuguese', nl: 'Dutch', dutch: 'Dutch',
};

export function normaliseLanguage(value) {
  const raw = text(value);
  if (!raw) return '';
  const known = LANGUAGES[raw.toLowerCase()];
  return known || raw.charAt(0).toUpperCase() + raw.slice(1);
}

function saleReference(raw, fallbackGrader, fallbackGrade) {
  if (raw == null) return null;
  if (typeof raw === 'number' || typeof raw === 'string') {
    const price = toNumber(raw);
    return price == null ? null : { grader: fallbackGrader, grade: fallbackGrade, price, date: null, url: null };
  }
  if (typeof raw !== 'object') return null;
  const price = toNumber(first(raw, ['priceGbp', 'price_gbp', 'price', 'valueGbp', 'value', 'amount', 'gbp', 'soldPrice', 'sold_price']));
  const low=toNumber(raw.low),high=toNumber(raw.high),date=isoDate(first(raw, ['date', 'soldAt', 'sold_at', 'soldOn', 'sold_on', 'asOf', 'as_of']));
  const range=price==null&&raw.status==='range'&&low>0&&high>low&&date;
  if (price == null && !range) return null;
  const parsed = parseGrade(first(raw, ['grade']) ?? fallbackGrade, first(raw, ['grader']) ?? fallbackGrader);
  return {
    grader: parsed.grader || fallbackGrader,
    grade: parsed.grade || fallbackGrade,
    price,
    ...(range?{low,high,status:'range'}:{}),
    date,
    url: safeImageUrl(first(raw, ['url', 'sourceUrl', 'source_url'])),
    verification: text(raw.verification),
  };
}

// Newest usable sold record per grader from the inventory pricing evidence.
function salesFromEvidence(evidence) {
  const newest = new Map();
  for (const source of Array.isArray(evidence?.sources) ? evidence.sources : []) {
    if (source?.kind !== 'sold') continue;
    for (const record of Array.isArray(source.records) ? source.records : []) {
      if (typeof record?.priceGbp !== 'number' || record.usableForPricing === false || record.priceIsUpperBound === true) continue;
      const grader = text(record.grader || source.grader).toUpperCase();
      if (!grader) continue;
      const time = Date.parse(record.date || '') || 0;
      const current = newest.get(grader);
      if (!current || time > current.time) {
        newest.set(grader, {
          time,
          sale: { grader, grade: text(record.grade || source.grade), price: record.priceGbp, date: isoDate(record.date), url: safeImageUrl(record.url) },
        });
      }
    }
  }
  return newest;
}

function population(raw) {
  const nested = first(raw, ['pop', 'population', 'acePopulation', 'ace_population', 'aceIdentity.population', 'ace_identity.population']);
  const inNested = (keys) => (nested && typeof nested === 'object' ? toNumber(first(nested, keys)) : null);
  const atGrade = toNumber(first(raw, ['popAtGrade', 'pop_at_grade', 'popThisGrade', 'pop_this_grade', 'gradePop', 'grade_pop', 'acePop', 'ace_pop']))
    ?? (nested && typeof nested === 'object' ? inNested(['atGrade', 'at_grade', 'thisGrade', 'this_grade', 'grade', 'count']) : toNumber(nested));
  const higher = toNumber(first(raw, ['popHigher', 'pop_higher', 'gradedHigher', 'graded_higher']))
    ?? inNested(['higher', 'gradedHigher', 'graded_higher', 'above']);
  const total = toNumber(first(raw, ['totalGraded', 'total_graded', 'totalAceGraded', 'total_ace_graded', 'aceTotal', 'ace_total', 'popTotal', 'pop_total']))
    ?? inNested(['total', 'totalGraded', 'total_graded', 'all']);
  if (atGrade == null && higher == null && total == null) return null;
  return { atGrade, higher, total };
}

const SYNONYMS = [
  [/\b(lv\.? ?x|level x)\b/, ' level x lvx lv.x'],
  [/\bδ|delta species\b/, ' delta species'],
  [/\b1st ed(ition)?\b/, ' 1st edition first edition'],
  [/\bshadowless\b/, ' shadowless'],
  [/\bjapanese\b/, ' jp jpn'],
  [/\bspecial illustration rare\b/, ' sir'],
  [/\bspecial art rare\b/, ' sar'],
  [/(?<!special )\billustration rare\b/, ' ir'],
  [/(?<!special )\bart rare\b/, ' ar'],
  [/\bfull art\b/, ' fullart'],
];
const SHORT_RARITIES = {sir:'Special Illustration Rare',sar:'Special Art Rare',ir:'Illustration Rare',ar:'Art Rare'};

export function searchText(value) {
  let result = text(value).toLowerCase();
  for (const [pattern, extra] of SYNONYMS) if (pattern.test(result)) result += extra;
  return result;
}

export function matchesSearch(haystack, tokens) {
  return tokens.every(token => ['sir','sar','ir','ar'].includes(token)
    ? new RegExp(`\\b${token}\\b`).test(haystack) : haystack.includes(token));
}

// Keep rarity, printing and edition separate: a Holo label alone does not
// establish rarity, and a blank edition must not become an assumed Unlimited.
function cardRarity(raw, variant) {
  const explicit = text(first(raw, ['rarity', 'aceIdentity.rarity', 'ace_identity.rarity']));
  const candidate = explicit || (/\brare\b|^(?:common|uncommon)$/i.test(variant) || SHORT_RARITIES[variant.toLowerCase()] ? variant : '');
  const label = candidate.replace(/\s+/g, ' ').trim();
  return SHORT_RARITIES[label.toLowerCase()] || Object.values(SHORT_RARITIES).find(value=>value.toLowerCase()===label.toLowerCase()) || label;
}

function cardEdition(value) {
  const label = text(value).replace(/\s+/g, ' ');
  if (/^(?:1st|first)(?: ed(?:ition)?)?$/i.test(label)) return '1st Edition';
  if (/^unlimited(?: edition)?$/i.test(label)) return 'Unlimited';
  if (/^shadowless$/i.test(label)) return 'Shadowless';
  return label;
}

function sourceOf(raw) {
  const explicit = text(first(raw, ['__source', 'ownership', 'source'])).toLowerCase();
  if (explicit === 'business' || explicit === 'personal') return explicit;
  if (raw.ownership === null && raw.cert_number) return 'personal';
  return explicit || 'inventory';
}

export function normaliseSlab(raw, position = 0) {
  if (!raw || typeof raw !== 'object') return null;
  const cert = text(first(raw, ['certNumber', 'cert_number', 'cert', 'certificateNumber', 'certificate_number'])).replace(/^#/, '');
  const name = text(first(raw, ['cardName', 'card_name', 'name', 'title']));
  if (!cert && !name) return null;
  const source = sourceOf(raw);
  const { grader, grade, gradeNum, gradeLabel } = parseGrade(
    first(raw, ['grade', 'gradeText', 'grade_text']),
    first(raw, ['grader', 'gradingCompany', 'grading_company']),
  );
  const set = text(first(raw, ['setName', 'set_name', 'set', 'expansion']));
  const number = text(first(raw, ['cardNumber', 'card_number', 'number'])).replace(/^#/, '');
  const releaseDate = first(raw, ['releaseDate', 'release_date', 'aceIdentity.releaseDate', 'ace_identity.releaseDate']);
  const year = toNumber(first(raw, ['year', 'aceIdentity.year', 'ace_identity.year']))
    ?? (/^\d{4}(?:-|$)/.test(text(releaseDate)) ? Number(text(releaseDate).slice(0, 4)) : null);
  const language = normaliseLanguage(first(raw, ['language', 'lang', 'aceIdentity.language', 'ace_identity.language']));
  const variant = text(first(raw, ['variant', 'finish', 'aceIdentity.variant', 'ace_identity.variant']));
  const rarity = cardRarity(raw, variant);
  const edition = cardEdition(first(raw, ['edition', 'aceIdentity.edition', 'ace_identity.edition']));
  const additionalInformation = text(first(raw, ['additionalInformation', 'additional_information', 'aceIdentity.additionalInformation', 'ace_identity.additionalInformation']));
  const category = text(first(raw, ['category', 'collectionName', 'collection_name']));
  const tags = Array.isArray(raw.tags) ? raw.tags.map(text).filter(Boolean) : [];

  const evidenceSales = salesFromEvidence(get(raw, 'market.evidence'));
  const sales = [];
  const explicitAce = saleReference(first(raw, ['aceLastSold', 'ace_last_sold', 'lastSold.ace', 'last_sold.ace']), 'ACE', grade);
  const explicitPsa = saleReference(first(raw, ['psaLastSold', 'psa_last_sold', 'lastSold.psa', 'last_sold.psa']), 'PSA', grade);
  const explicitSame = (grader === 'ACE' ? explicitAce : grader === 'PSA' ? explicitPsa : null)
    || saleReference(first(raw, ['lastSale', 'last_sale']), grader, grade);
  const sameGrader = explicitSame || evidenceSales.get(grader)?.sale || null;
  const aceReference = explicitAce || evidenceSales.get('ACE')?.sale || (grader === 'ACE' ? sameGrader : null);
  const psaReference = explicitPsa || evidenceSales.get('PSA')?.sale || (grader === 'PSA' ? sameGrader : null);
  const listed=raw.shopifyListing;
  const shopifyListing=listed?.currency==='GBP'&&toNumber(listed.priceGbp)>0&&isoDate(listed.checkedAt)?{...listed,priceGbp:toNumber(listed.priceGbp),url:safeImageUrl(listed.url)}:null;
  if (sameGrader) sales.push({ ...sameGrader, sameGrader: true });
  for (const sale of [aceReference, psaReference]) {
    if (sale && sale.grader !== grader) sales.push({ ...sale, sameGrader: false });
  }
  for (const [otherGrader, entry] of evidenceSales) {
    if (otherGrader !== grader && !['ACE', 'PSA'].includes(otherGrader)) sales.push({ ...entry.sale, sameGrader: false });
  }

  const explicitValue = toNumber(first(raw, ['valueGbp', 'value_gbp', 'marketValue', 'market_value', 'estimatedValue', 'estimated_value', 'value']));
  const marketValue = toNumber(get(raw, 'market.valueGbp'));
  const vaultValue = toNumber(first(raw, ['current_value', 'currentValue']));
  let value = null;
  let valueLabel = '';
  if (explicitValue != null) { value = explicitValue; valueLabel = 'Market value'; }
  else if (marketValue != null) { value = marketValue; valueLabel = 'Market value'; }
  else if (vaultValue != null) { value = vaultValue; valueLabel = 'Vault value'; }
  else if (sameGrader) { value = sameGrader.price; valueLabel = `Last ${grader} ${grade} sale`; }

  const cost = toNumber(first(raw, ['acquisition.costGbp', 'purchase_price', 'purchasePrice', 'costGbp', 'cost']));
  const addedAt = isoDate(first(raw, ['createdAt', 'created_at', 'acquisition.purchasedOn', 'purchase_date', 'updatedAt', 'updated_at']));
  const supplier = text(first(raw, ['acquisition.supplier', 'supplier']));
  const location = text(first(raw, ['acquisition.location', 'location']));
  const storageLocation=savedStorage(raw);
  const notes = text(first(raw, ['acquisition.notes', 'notes']));
  const ownSubmission=raw.ownSubmission&&typeof raw.ownSubmission.number==='string'?raw.ownSubmission:null;
  const intakeGroup=raw.intakeGroup||null;
  const front = safeImageUrl(first(raw, ['images.front', 'imageFront', 'image_front', 'ace_image_front', 'frontImage', 'front_image'])) || aceImage(cert, 'front');
  const back = safeImageUrl(first(raw, ['images.back', 'imageBack', 'image_back', 'ace_image_back', 'backImage', 'back_image'])) || aceImage(cert, 'back');

  const haystack = searchText([
    name, set, number, number && `#${number}`, cert, grader && grade && `${grader} ${grade}`, grader && grade && `${grader}${grade}`,
    gradeLabel, language, rarity, variant, edition, additionalInformation, category, year, supplier, location,storageLocation.label,storageLocation.detail,storageLocation.compact,storageLocation.boxCode, notes, ownSubmission?.number,ownSubmission?.name,intakeGroup?.label, ...tags,
  ].filter(Boolean).join(' '));

  return {
    key: `${source}:${text(raw.id) || cert || `row-${position}`}`,
    id: raw.id ?? null,
    source,
    cert,
    name: name || (cert ? `Cert ${cert}` : 'Untitled slab'),
    set,
    number,
    year,
    language,
    hasGuard:typeof raw.hasGuard==='boolean'?raw.hasGuard:null,
    rarity,
    variant,
    edition,
    additionalInformation,
    category,
    tags,
    grader,
    grade,
    gradeNum,
    gradeLabel,
    gradeKey: grader && grade ? `${grader} ${grade}` : '',
    images: { front, back },
    value,
    valueLabel,
    valueAsOf: isoDate(first(raw, ['market.asOf', 'last_valued_at', 'lastValuedAt', 'valueAsOf', 'value_as_of'])),
    aceReference,
    psaReference,
    shopifyListing,
    sales,
    pop: population(raw),
    cost,
    profit: value != null && cost != null ? value - cost : null,
    addedAt,
    addedAtMs: addedAt ? Date.parse(addedAt) : 0,
    supplier,
    location,
    storageLocation,
    notes,
    ownSubmission,intakeGroup,
    intendedUse:raw.intendedUse||'unassigned',updatedAt:raw.updatedAt||null,
    physicalIssue:raw.physicalIssue||raw.physical_issue||null,physicalIssueRevision:raw.physicalIssueRevision||raw.physical_issue_revision||0,stockStatus:raw.stockStatus||raw.stock_status||null,handoffState:raw.handoffState||raw.handoff_state||'none',
    haystack,
  };
}

export function normaliseSlabs(rows) {
  const seen = new Set();
  const slabs = [];
  (Array.isArray(rows) ? rows : []).forEach((row, position) => {
    const slab = normaliseSlab(row, position);
    if (!slab || seen.has(slab.key)) return;
    seen.add(slab.key);
    slabs.push(slab);
  });
  return slabs;
}

export function referenceSale(slab, grader = 'PSA') {
  const sale = grader === 'ACE' ? slab.aceReference : slab.psaReference;
  if (sale?.grader !== grader || slab.gradeNum == null || toNumber(sale.grade) !== slab.gradeNum) return null;
  if (!(Number.isFinite(sale.price)&&sale.price>0)&&!(sale.status==='range'&&sale.low>0&&sale.high>sale.low&&sale.date))return null;
  return sale;
}

export const minimumValue = slab => slab.value ?? slab.valueLow ?? null;
export const hasValue = slab => Number.isFinite(minimumValue(slab));

// A display-only projection keeps sorting, ranges, totals and card values together.
export function withPriceReference(slabs, grader = 'PSA') {
  const basis = grader === 'ACE' ? 'ACE' : 'PSA';
  return slabs.map(slab => {
    const sale = referenceSale(slab, basis);
    const listing=!sale?slab.shopifyListing:null;
    const value = sale?.price ?? listing?.priceGbp ?? null;
    return { ...slab, value, valueLow:value??sale?.low??null,valueHigh:value??sale?.high??null,
      valueLabel:listing?'Shopify listed price':sale?.status==='range'?`Latest ${basis} ${slab.grade} sales · range`:`Last ${basis} ${slab.grade} sale`, valueAsOf: sale?.date ?? listing?.checkedAt ?? null,
      valuationBasis:listing?'shopify_listing':sale?'sold':null,
      profit: !listing&&value != null && slab.cost != null ? value - slab.cost : null };
  });
}

function amount(digits, suffix) {
  const base = Number(digits);
  return suffix === 'k' ? base * 1000 : suffix === 'm' ? base * 1000000 : base;
}

// Words narrow by name, set, number, cert, grade ("ace 10"), language or category.
// Value: ">500", "<2k", "£1k+".
export function parseQuery(query) {
  const tokens = [];
  let min = null;
  let max = null;
  const input=String(query || '').toLowerCase()
    .replace(/\bspecial\s+illustration\s+rare\b/g,'sir')
    .replace(/\bspecial\s+art\s+rare\b/g,'sar')
    .replace(/\billustration\s+rare\b/g,'ir')
    .replace(/\bart\s+rare\b/g,'ar');
  for (const part of input.split(/\s+/)) {
    const token = part.replace(/,/g, '');
    if (!token) continue;
    let match = token.match(/^>=?£?(\d+(?:\.\d+)?)([km])?$/);
    if (match) { min = amount(match[1], match[2]); continue; }
    match = token.match(/^<=?£?(\d+(?:\.\d+)?)([km])?$/);
    if (match) { max = amount(match[1], match[2]); continue; }
    match = token.match(/^£(\d+(?:\.\d+)?)([km])?\+$/) || token.match(/^(\d+(?:\.\d+)?)([km])\+$/);
    if (match) { min = amount(match[1], match[2]); continue; }
    tokens.push(token.replace(/^#/, ''));
  }
  return { tokens, min, max };
}

export const EMPTY_FILTERS = Object.freeze({ source: 'all', graders: [], grades: [], languages: [], sets:[],rarities:[],variants:[],editions:[],years:[],labels:[],categories:[],hiddenCategories:[],hiddenLabels:[],hiddenNoteQuery:'',priceStatus:[],noteQuery:'', min: '', max: '', priceBasis: 'PSA' });

// These are view exclusions, not a persistent lock on the underlying slab.
export function isHiddenByInventoryFilters(slab, filters) {
  const note = filters.hiddenNoteQuery?.trim().toLowerCase();
  return !!((filters.hiddenCategories || []).includes(slab.category)
    || (filters.hiddenLabels || []).some(label => (slab.tags || []).includes(label))
    || (note && (slab.notes || '').toLowerCase().includes(note)));
}

// Removal is final for current stock until the slab is physically scanned back in.
// Keep the old helper name for saved callers, but never let a persisted view
// preference restore removed records to browsing, totals or new pick selection.
const OUT_OF_INVENTORY = new Set(['picked', 'transferred', 'removed', 'sold', 'traded', 'returned']);
export function isTakenOut(slab) {
  return OUT_OF_INVENTORY.has(slab.stockStatus);
}

export function filterByInventoryScope(slabs) {
  return slabs.filter(slab => !isTakenOut(slab));
}

export function activeFilterCount(filters) {
  return (filters.source !== 'all' ? 1 : 0) + filters.graders.length + filters.grades.length + filters.languages.length
    + (filters.labels?.length||0)+(filters.categories?.length||0)+(filters.noteQuery?.trim()?1:0)
    + (filters.hiddenCategories?.length||0)+(filters.hiddenLabels?.length||0)+(filters.hiddenNoteQuery?.trim()?1:0)
    + ['sets','rarities','variants','editions','years','priceStatus'].reduce((sum,key)=>sum+(filters[key]?.length||0),0)
    + (toNumber(filters.min) != null ? 1 : 0) + (toNumber(filters.max) != null ? 1 : 0) + (filters.priceBasis === 'ACE' ? 1 : 0);
}

function byNullableNumber(direction, pick) {
  return (left, right) => {
    const a = pick(left);
    const b = pick(right);
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    return direction * (a - b);
  };
}

const byValueDesc = byNullableNumber(-1, minimumValue);

export const SORTS = [
  { id: 'value-desc', label: 'Value: high to low', compare: byValueDesc },
  { id: 'value-asc', label: 'Value: low to high', compare: byNullableNumber(1, minimumValue) },
  { id: 'recent', label: 'Recently added', compare: (a, b) => b.addedAtMs - a.addedAtMs || byValueDesc(a, b) },
  { id: 'grade-desc', label: 'Grade: high to low', compare: (a, b) => byNullableNumber(-1, (slab) => slab.gradeNum)(a, b) || byValueDesc(a, b) },
  { id: 'rarest', label: 'Rarest first', compare: (a, b) => byNullableNumber(1, (slab) => slab.pop?.atGrade)(a, b) || byValueDesc(a, b) },
  { id: 'profit-desc', label: 'Profit: high to low', compare: (a, b) => byNullableNumber(-1, (slab) => slab.profit)(a, b) || byValueDesc(a, b) },
  { id: 'name', label: 'Name: A to Z', compare: (a, b) => a.name.localeCompare(b.name, 'en-GB', { sensitivity: 'base' }) || byValueDesc(a, b) },
];

export function selectSlabs(slabs, { query = '', filters = EMPTY_FILTERS, sort = 'value-desc' } = {}) {
  const { tokens, min: queryMin, max: queryMax } = parseQuery(query);
  const graders = new Set(filters.graders);
  const grades = new Set(filters.grades);
  const languages = new Set(filters.languages);
  const min = queryMin ?? toNumber(filters.min);
  const max = queryMax ?? toNumber(filters.max);
  const matches = filterByInventoryScope(slabs).filter((slab) => {
    if (isHiddenByInventoryFilters(slab, filters)) return false;
    if (filters.source !== 'all' && slab.source !== filters.source) return false;
    if (graders.size && !graders.has(slab.grader)) return false;
    if (grades.size && !grades.has(slab.gradeKey)) return false;
    if (languages.size && !languages.has(slab.language || 'Unknown')) return false;
    if (filters.priceStatus?.length && !filters.priceStatus.includes(hasValue(slab) ? 'priced' : 'unpriced')) return false;
    for (const [key,value] of [['sets',slab.set],['rarities',slab.rarity],['variants',slab.variant],['editions',slab.edition],['years',slab.year == null ? '' : String(slab.year)]]) {
      if (filters[key]?.length && !filters[key].includes(value || 'Not recorded')) return false;
    }
    if(filters.labels?.length&&!filters.labels.some(l=>slab.tags.includes(l)))return false;
    if(filters.categories?.length&&!filters.categories.includes(slab.category))return false;
    if(filters.noteQuery?.trim()&&!slab.notes.toLowerCase().includes(filters.noteQuery.trim().toLowerCase()))return false;
    if (min != null && (minimumValue(slab) == null || minimumValue(slab) < min)) return false;
    if (max != null && (minimumValue(slab) == null || minimumValue(slab) > max)) return false;
    return tokens.every(token=>SHORT_RARITIES[token] ? slab.rarity===SHORT_RARITIES[token] : matchesSearch(slab.haystack,[token]));
  });
  const order = SORTS.find((option) => option.id === sort) || SORTS[0];
  return matches.sort(order.compare);
}

function countBy(slabs, pick) {
  const counts = new Map();
  for (const slab of slabs) {
    const key = pick(slab);
    if (key) counts.set(key, (counts.get(key) || 0) + 1);
  }
  return [...counts.entries()].map(([key, count]) => ({ key, count }));
}

export function facetsFor(slabs) {
  const gradeOrder = (key) => Number(key.split(' ')[1]) || 0;
  return {
    priceStatus: [
      { key: 'priced', label: 'Has price', count: slabs.filter(hasValue).length },
      { key: 'unpriced', label: 'Needs pricing', count: slabs.filter(slab => !hasValue(slab)).length },
    ],
    sources: countBy(slabs, (slab) => slab.source).sort((a, b) => b.count - a.count),
    graders: countBy(slabs, (slab) => slab.grader).sort((a, b) => b.count - a.count),
    grades: countBy(slabs, (slab) => slab.gradeKey).sort((a, b) => a.key.split(' ')[0].localeCompare(b.key.split(' ')[0]) || gradeOrder(b.key) - gradeOrder(a.key)),
    languages: countBy(slabs, (slab) => slab.language || 'Unknown').sort((a, b) => b.count - a.count),
    sets: countBy(slabs, slab=>slab.set||'Not recorded').sort((a,b)=>a.key.localeCompare(b.key)),
    rarities: countBy(slabs, slab=>slab.rarity||'Not recorded').sort((a,b)=>a.key.localeCompare(b.key)),
    variants: countBy(slabs, slab=>slab.variant||'Not recorded').sort((a,b)=>a.key.localeCompare(b.key)),
    editions: countBy(slabs, slab=>slab.edition||'Not recorded').sort((a,b)=>a.key.localeCompare(b.key)),
    years: countBy(slabs, slab=>slab.year == null?'Not recorded':String(slab.year)).sort((a,b)=>Number(b.key)-Number(a.key)||a.key.localeCompare(b.key)),
    categories: countBy(slabs, (slab) => slab.category).sort((a, b) => b.count - a.count),
    labels:[...new Set(slabs.flatMap(s=>s.tags))].map(key=>({key,count:slabs.filter(s=>s.tags.includes(key)).length})).sort((a,b)=>b.count-a.count),
  };
}

export function totals(slabs) {
  let value = 0;
  let valueLow=0,valueHigh=0,ranges=0;
  let valued = 0;
  let cost = 0;
  let costed = 0;
  for (const slab of slabs) {
    if (slab.value != null) value += slab.value;
    if (hasValue(slab)) { valueLow+=minimumValue(slab);valueHigh+=slab.value??slab.valueHigh;valued++;if(slab.value==null)ranges++; }
    if (slab.cost != null) { cost += slab.cost; costed += 1; }
  }
  return { count: slabs.length, value, valueLow,valueHigh,ranges,valued, cost, costed };
}

export function psaReferenceTotals(slabs) {
  let value = 0;
  let valued = 0;
  for (const slab of slabs) {
    const sale = referenceSale(slab);
    if (!sale || !Number.isFinite(sale.price)) continue;
    value += sale.price;
    valued += 1;
  }
  return { count: slabs.length, value: valued ? value : null, valued };
}

const GBP_WHOLE = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 });
const GBP_PENCE = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2, maximumFractionDigits: 2 });
const COUNT = new Intl.NumberFormat('en-GB');

export function money(value) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  return Math.abs(value) < 100 && value % 1 !== 0 ? GBP_PENCE.format(value) : GBP_WHOLE.format(Math.round(value));
}

export function moneyRange(low,high=low) {
  if(!Number.isFinite(low)||!Number.isFinite(high))return '—';
  if(low===high)return money(low);
  const format=Math.round(low)===Math.round(high)?v=>GBP_PENCE.format(v):money;
  return `${format(low)}–${format(high)}`;
}
export const saleMoney = sale => moneyRange(sale?.price??sale?.low,sale?.price??sale?.high);
export const slabMoney = slab => moneyRange(minimumValue(slab),slab.value??slab.valueHigh);

export function signedMoney(value) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  return `${value >= 0 ? '+' : '−'}${money(Math.abs(value))}`;
}

export function count(value) {
  return COUNT.format(value || 0);
}

export function shortDate(value) {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function sourceLabel(source) {
  return source === 'business' ? 'Business stock' : source === 'personal' ? 'Personal vault' : 'Inventory';
}
